import React, { useState, useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Container, Card, Spinner, Row, Col, Form, Modal } from "react-bootstrap";
import {
  Info,
  X,
  ListChecks,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
  User,
  ArrowRight,
  Globe,
  Eye,
  RefreshCw,
} from "lucide-react";

import { Formik } from "formik";
import * as Yup from "yup";

import { useTranslation } from "react-i18next";

import { showAlert } from "store/reducers/alertReducer";

import { initiateTapOnboarding } from "store/actions/tapOnboardingSlice";

import { getCompanyInfo } from "store/actions/authSlice";

import CommonTitleBar from "components/common/CommonTitleBar";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";

const LANGUAGE_OPTIONS = ["en", "ar"];

const NAME_REGEX_EN = /^[a-zA-Z\s]+$/;
const NAME_REGEX_EN_OPTIONAL = /^[a-zA-Z\s]*$/;
const NAME_REGEX_AR = /^[؀-ۿ\s]+$/;
const NAME_REGEX_AR_OPTIONAL = /^[؀-ۿ\s]*$/;

// Name/brand fields must match the script of the selected lead language (en/ar).
// `required` picks the non-empty regex variant; optional fields allow an empty string.
const nameByLanguage = (schema, { required } = {}) =>
  schema.when("language", {
    is: "ar",
    then: (s) =>
      s.matches(
        required ? NAME_REGEX_AR : NAME_REGEX_AR_OPTIONAL,
        "Arabic letters only, no numbers or symbols",
      ),
    otherwise: (s) =>
      s.matches(
        required ? NAME_REGEX_EN : NAME_REGEX_EN_OPTIONAL,
        "English letters only, no numbers or symbols",
      ),
  });

const leadDetailsSchema = Yup.object().shape({
  language: Yup.string()
    .oneOf(LANGUAGE_OPTIONS, "Select a valid language")
    .optional(),

  title: nameByLanguage(Yup.string().max(20, "Max 20 characters")),

  firstName: nameByLanguage(
    Yup.string()
      .required("First name is required")
      .min(2, "At least 2 characters")
      .max(50, "Max 50 characters"),
    { required: true },
  ).test("no-spaces", "Cannot be only spaces", (v) => v?.trim().length > 0),

  middleName: nameByLanguage(Yup.string().max(50, "Max 50 characters")),

  lastName: nameByLanguage(Yup.string().max(50, "Max 50 characters")),

  email: Yup.string()
    .required("Email is required")
    .email("Enter a valid email address")
    .max(100, "Max 100 characters"),

  phoneCode: Yup.string()
    .required("Phone code is required")
    .matches(/^\d{1,4}$/, "Digits only, 1–4 digits (e.g. 966)"),

  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{7,15}$/, "Must be 7–15 digits"),

  brandName: nameByLanguage(
    Yup.string()
      .required("Brand name is required")
      .min(2, "At least 2 characters")
      .max(100, "Max 100 characters"),
    { required: true },
  ).test("no-spaces", "Cannot be only spaces", (v) => v?.trim().length > 0),

  country: Yup.string()
    .matches(/^[A-Za-z]{2}$/, "Must be a 2-letter country code (e.g. SA)")
    .optional(),
});

function TapOnboarding() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { organizationManagerDetails, orgnisationDetails } = useSelector(
    (state) => state.auth,
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const hasAutoOpenedInfoModal = useRef(false);

  const tap = organizationManagerDetails?.merchantInfo?.tapInfo || {};
  const status = tap?.onboardingStatus || "not_started";
  const onboardingNotStarted = status === "not_started";

  // Statuses where the lead-details form should be shown so the user can submit/retry.
  const formStatuses = ["not_started", "lead_created", "merchant_rejected"];
  // Statuses where Tap onboarding is underway and the user can jump back into the Tap portal.
  const resumableStatuses = [
    "connect_created",
    "connect_sent",
    "onboarding_in_progress",
  ];

  const statusConfig = {
    not_started: {
      label: t("Not Started"),
      pillClass: "status_not_started",
      icon: Clock,
    },
    lead_created: {
      label: t("Getting Started"),
      pillClass: "status_in_progress",
      icon: Loader2,
    },
    connect_created: {
      label: t("In Progress"),
      pillClass: "status_in_progress",
      icon: Loader2,
    },
    connect_sent: {
      label: t("In Progress"),
      pillClass: "status_in_progress",
      icon: Loader2,
    },
    onboarding_in_progress: {
      label: t("In Progress"),
      pillClass: "status_in_progress",
      icon: Loader2,
    },
    under_review: {
      label: t("Under Review"),
      pillClass: "status_under_review",
      icon: Eye,
    },
    active: {
      label: t("Active"),
      pillClass: "status_active",
      icon: CheckCircle2,
    },
    merchant_rejected: {
      label: t("Rejected"),
      pillClass: "status_rejected",
      icon: XCircle,
    },
  };

  const StatusIcon = statusConfig[status]?.icon || Clock;

  // Index of the onboarding step currently in progress, used to drive the stepper below.
  const stepStatusIndex = {
    not_started: 0,
    lead_created: 1,
    connect_created: 2,
    connect_sent: 2,
    onboarding_in_progress: 2,
    under_review: 3,
    active: 4,
    merchant_rejected: 0,
  };
  const activeStepIndex = stepStatusIndex[status] ?? 0;
  const erroredStepIndex = status === "merchant_rejected" ? 2 : null;

  const statusGuidance = {
    not_started: t(
      "Fill in your details below and click 'Start Onboarding' to connect with Tap Payments.",
    ),
    lead_created: t(
      "Your lead was created with Tap but the connection wasn't finished. Click 'Continue Setup' below to try again.",
    ),
    connect_created: t(
      "Your onboarding is in progress. Click 'Continue Onboarding' to complete the setup on the Tap portal.",
    ),
    connect_sent: t(
      "We've sent your onboarding link. Click 'Continue Onboarding' to complete verification on the Tap portal.",
    ),
    onboarding_in_progress: t(
      "You're currently completing verification on the Tap portal. Click 'Continue Onboarding' to resume where you left off.",
    ),
    under_review: t(
      "Your details have been submitted and are under review by Tap. This can take a few business days — check back here for updates.",
    ),
    active: t(
      "Your account is connected and active. You can now accept payments through Tap.",
    ),
    merchant_rejected: t(
      "Your onboarding was rejected. Update your details below and click 'Retry' to try again.",
    ),
  };

  const showForm = formStatuses.includes(status);

  const submitButtonLabel =
    status === "merchant_rejected"
      ? t("Retry Onboarding")
      : status === "lead_created"
        ? t("Continue Setup")
        : t("Start Onboarding");

  const onboardingSteps = [
    {
      icon: ListChecks,
      title: t("Add your details"),
      description: t("Fill in your brand and contact information below."),
    },
    {
      icon: CreditCard,
      title: t("Start onboarding"),
      description: t(
        "Click 'Start Onboarding' to create your Tap merchant connect request.",
      ),
    },
    {
      icon: ShieldCheck,
      title: t("Verify with Tap"),
      description: t(
        "You'll be redirected to the Tap portal to complete verification (KYC).",
      ),
    },
    {
      icon: CheckCircle2,
      title: t("Start accepting payments"),
      description: t(
        "Once approved, your status will show as Active and you can start accepting payments.",
      ),
    },
  ];

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    if (pageLoading || hasAutoOpenedInfoModal.current) return;
    hasAutoOpenedInfoModal.current = true;
    if (onboardingNotStarted) {
      setShowInfoModal(true);
    }
  }, [pageLoading, onboardingNotStarted]);

  const fetchCompanyInfo = async () => {
    try {
      setPageLoading(true);
      await dispatch(getCompanyInfo());
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Failed to load company info"),
        }),
      );
    } finally {
      setPageLoading(false);
    }
  };

  const getInitialValues = () => ({
    language: "en",
    title: "",
    firstName: orgnisationDetails?.name || "",
    middleName: "",
    lastName: "",
    email: orgnisationDetails?.email || "",
    phoneCode: orgnisationDetails?.phoneCode?.replace("+", "") || "",
    phoneNumber: orgnisationDetails?.phoneNumber || "",
    brandName: orgnisationDetails?.companyDetails?.companyName || "",
    country: "SA",
  });

  const handleStartOnboarding = async (leadDetails) => {
    try {
      setBtnLoading(true);

      const response = await dispatch(initiateTapOnboarding({ leadDetails }));
      const result = response?.payload;

      if (result?.success) {
        const connectUrl = result?.data?.connectUrl;
        await dispatch(getCompanyInfo());
        setRedirecting(true);
        window.location.href = connectUrl;
      } else {
        dispatch(
          showAlert({
            type: "error",
            text: result?.message || t("Failed to start onboarding"),
          }),
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Something went wrong. Please try again."),
        }),
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleResumeOnboarding = () => {
    if (tap?.connectUrl) {
      setRedirecting(true);
      window.location.href = tap.connectUrl;
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setRefreshing(true);
      await dispatch(getCompanyInfo());
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Failed to refresh status"),
        }),
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Container className="merchant_onboarding_page">
      <CommonTitleBar title={t("Merchant Onboarding")} />

      <Card className="p-4 shadow-sm mt-3 onboarding_header_card">
        <div className="d-flex align-items-start gap-3">
          <span className="d-flex align-items-center justify-content-center bg_primary onboarding_icon_badge">
            <CreditCard size={20} color="#fff" />
          </span>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="mb-0">{t("Tap Payment Setup")}</h5>
              <Info
                size={16}
                className="text_primary c_p"
                onClick={() => setShowInfoModal(true)}
                role="button"
                aria-label={t("Onboarding information")}
              />
            </div>
            <p className="text-muted mt-1 mb-2">
              {t("Manage your Tap Payments merchant connection.")}
            </p>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                className={`status_pill ${statusConfig[status]?.pillClass || "status_not_started"}`}
              >
                <StatusIcon size={13} />
                {statusConfig[status]?.label || status}
              </span>
              {tap?.merchantId && (
                <span className="merchant_id_chip">
                  <span className="text-muted">{t("Merchant ID")}:</span>
                  <b>{tap.merchantId}</b>
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-muted small mt-3 mb-0">{statusGuidance[status]}</p>

        {/* Onboarding progress stepper */}
        <div className="onboarding_stepper">
          {onboardingSteps.map(({ icon: StepIcon, title }, idx) => {
            const isComplete = idx < activeStepIndex;
            const isActive = idx === activeStepIndex;
            const isError = idx === erroredStepIndex;

            return (
              <React.Fragment key={title}>
                <div
                  className={`onboarding_step ${isComplete ? "is-complete" : ""} ${
                    isActive ? "is-active" : ""
                  } ${isError ? "is-error" : ""}`}
                >
                  <span className="step_icon">
                    {isComplete && !isError ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <StepIcon size={16} />
                    )}
                  </span>
                  <span className="step_label">{title}</span>
                </div>
                {idx < onboardingSteps.length - 1 && (
                  <span
                    className={`step_connector ${isComplete ? "is-complete" : ""}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <hr className="mt-1" />

        {/* Lead details form — only for actionable states */}
        {showForm && (
          <Formik
            initialValues={getInitialValues()}
            validationSchema={leadDetailsSchema}
            onSubmit={(values) => handleStartOnboarding(values)}
            enableReinitialize
          >
            {({ handleSubmit, touched, errors, values, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <div className="form_section">
                  <div className="form_section_title">
                    <Globe size={16} className="text_primary" />
                    {t("Language")}
                  </div>
                  <Row className="mb-3">
                    <Col md={4}>
                      <CustomLabel
                        htmlFor="language"
                        label={t("Name & Brand Language")}
                      />
                      <Form.Select
                        id="language"
                        value={values.language}
                        onChange={(e) =>
                          setFieldValue("language", e.target.value)
                        }
                      >
                        <option value="en">{t("English")}</option>
                        <option value="ar">{t("Arabic")}</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </div>

                <div className="form_section">
                  <div className="form_section_title">
                    <ListChecks size={16} className="text_primary" />
                    {t("Business Details")}
                  </div>
                  <Row className="mb-3">
                    <Col md={6}>
                      <CustomLabel
                        htmlFor="brandName"
                        label={t("Brand Name")}
                      />
                      <CustomField
                        name="brandName"
                        placeholder={t("Brand Name")}
                        touched={touched}
                        errors={errors}
                        isArabic={values.language === "ar"}
                      />
                    </Col>
                    <Col md={6}>
                      <CustomLabel
                        htmlFor="country"
                        label={t("Country Code")}
                      />
                      <CustomField
                        name="country"
                        placeholder={t("e.g. SA")}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="form_section">
                  <div className="form_section_title">
                    <User size={16} className="text_primary" />
                    {t("Contact Person")}
                  </div>
                  <Row className="mb-3">
                    <Col md={3}>
                      <CustomLabel htmlFor="title" label={t("Title")} />
                      <CustomField
                        name="title"
                        placeholder={t("e.g. Mr.")}
                        touched={touched}
                        errors={errors}
                        isArabic={values.language === "ar"}
                      />
                    </Col>
                    <Col md={3}>
                      <CustomLabel
                        htmlFor="firstName"
                        label={t("First Name")}
                      />
                      <CustomField
                        name="firstName"
                        placeholder={t("First Name")}
                        touched={touched}
                        errors={errors}
                        isArabic={values.language === "ar"}
                      />
                    </Col>
                    <Col md={3}>
                      <CustomLabel
                        htmlFor="middleName"
                        label={t("Middle Name")}
                      />
                      <CustomField
                        name="middleName"
                        placeholder={t("Middle Name")}
                        touched={touched}
                        errors={errors}
                        isArabic={values.language === "ar"}
                      />
                    </Col>
                    <Col md={3}>
                      <CustomLabel htmlFor="lastName" label={t("Last Name")} />
                      <CustomField
                        name="lastName"
                        placeholder={t("Last Name")}
                        touched={touched}
                        errors={errors}
                        isArabic={values.language === "ar"}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <CustomLabel htmlFor="email" label={t("Email")} />
                      <CustomField
                        name="email"
                        type="email"
                        placeholder={t("Email")}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    <Col md={2}>
                      <CustomLabel
                        htmlFor="phoneCode"
                        label={t("Phone Code")}
                      />
                      <CustomField
                        name="phoneCode"
                        placeholder={t("e.g. 966")}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <CustomLabel
                        htmlFor="phoneNumber"
                        label={t("Phone Number")}
                      />
                      <CustomField
                        name="phoneNumber"
                        placeholder={t("Phone Number")}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="main_btn d-inline-flex align-items-center justify-content-center gap-2"
                    disabled={btnLoading}
                  >
                    {btnLoading ? <Spinner size="sm" /> : submitButtonLabel}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* Resume button for statuses where the Tap portal flow can be continued */}
        {resumableStatuses.includes(status) && (
          <div className="d-flex justify-content-end align-items-center gap-3">
            <button
              type="button"
              className="onboarding_refresh_btn d-inline-flex align-items-center gap-2"
              onClick={handleRefreshStatus}
              disabled={refreshing}
            >
              <RefreshCw size={14} className={refreshing ? "is-spinning" : ""} />
              {t("Refresh Status")}
            </button>
            <button
              className="main_btn d-inline-flex align-items-center justify-content-center gap-2"
              onClick={handleResumeOnboarding}
            >
              {t("Continue Onboarding")}
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Under review panel — nothing actionable except manually re-checking the status */}
        {status === "under_review" && (
          <div className="onboarding_review_panel mt-3 d-flex align-items-start gap-3">
            <Eye size={22} className="flex-shrink-0" />
            <div className="flex-grow-1">
              <strong>{t("Under Review by Tap")}</strong>
              <p className="text-muted small mt-1 mb-2">
                {t(
                  "We'll update your status automatically once Tap finishes reviewing your application.",
                )}
              </p>
              <button
                type="button"
                className="onboarding_refresh_btn d-inline-flex align-items-center gap-2"
                onClick={handleRefreshStatus}
                disabled={refreshing}
              >
                <RefreshCw
                  size={14}
                  className={refreshing ? "is-spinning" : ""}
                />
                {t("Refresh Status")}
              </button>
            </div>
          </div>
        )}

        {/* Success panel */}
        {status === "active" && (
          <div className="onboarding_success_panel mt-3 d-flex align-items-start gap-3">
            <CheckCircle2 size={22} className="flex-shrink-0" />
            <div>
              <strong>{t("Tap Connected Successfully")}</strong>
              <p className="text-muted small mt-1 mb-0">
                {t(
                  "Your merchant account is active. Payments made by customers will be processed through Tap.",
                )}
              </p>
            </div>
          </div>
        )}
      </Card>

      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        className="common_modal"
      >
        <div className="modal_header g_between">
          <h2 className="fs_17 mb-0">{t("Onboarding with Tap Payments")}</h2>
          <X
            onClick={() => setShowInfoModal(false)}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body">
          <p className="text-muted mb-4">
            {t(
              "Follow these steps to connect your account and start accepting payments through Tap:",
            )}
          </p>

          <div className="d-flex flex-column gap-3">
            {onboardingSteps.map(({ icon: StepIcon, title, description }, idx) => (
              <div className="d-flex align-items-start gap-3" key={title}>
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle bg_primary"
                  style={{ width: 34, height: 34 }}
                >
                  <StepIcon size={17} color="#fff" />
                </span>
                <div>
                  <p className="mb-0 fw-semibold">
                    {idx + 1}. {title}
                  </p>
                  <p className="text-muted small mb-0">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="main_btn"
              onClick={() => setShowInfoModal(false)}
            >
              {t("Got it")}
            </button>
          </div>
        </div>
      </Modal>

      {redirecting && (
        <div className="tap_redirect_overlay">
          <div className="tap_redirect_overlay_content">
            <Spinner animation="border" />
            <p className="fw-semibold mb-1 mt-3">
              {t("Redirecting you to Tap Payments...")}
            </p>
            <p className="text-muted small mb-0">
              {t("Please wait, this will only take a moment.")}
            </p>
          </div>
        </div>
      )}
    </Container>
  );
}

export default TapOnboarding;
